/**
 * Coordinates run cancellation and stale-run detection.
 *
 * On conversation load, checks backend status. If a run is already in
 * progress (e.g. after page refresh), polls until it finishes and reloads
 * history so the UI reflects the completed run.
 *
 * For cancellation, aborts the local SSE stream, notifies the backend,
 * and polls until the backend settles before reloading history.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { HttpAgent } from "@ag-ui/client";
import type { AssistantRuntime } from "@assistant-ui/react";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import { fetchConversationHistory } from "./useConversationHistory";

const POLL_INTERVAL_MS = 3000;

interface UseRunCancellationOptions {
  api: AssistantApi;
  agent: HttpAgent;
  runtime: AssistantRuntime;
  conversationId: string | null;
  /** Suppress abort-related errors in useRunError during cancel. */
  clearError?: () => void;
}

interface UseRunCancellationReturn {
  /** The conversation is running on a remote replica (not this browser tab). */
  isRunningRemotely: boolean;
  /** Cancel the current run (local or remote). */
  onCancelRun: () => void;
  /** True while waiting for backend to acknowledge cancellation. */
  isCancelling: boolean;
}

function isInProgress(status: string | null | undefined): boolean {
  return status === "MESSAGE_IN_PROGRESS" || status === "MESSAGE_CANCELLED";
}

export function useRunCancellation({
  api,
  agent,
  runtime,
  conversationId,
  clearError,
}: UseRunCancellationOptions): UseRunCancellationReturn {
  const [isCancelling, setIsCancelling] = useState(false);
  const [isRunningRemotely, setIsRunningRemotely] = useState(false);

  // Abort handle for any active polling loop.
  const abortPollingRef = useRef<(() => void) | null>(null);

  const reloadHistory = useCallback(
    async (cid: string) => {
      try {
        const messages = await fetchConversationHistory(cid);
        if (messages.length > 0) {
          runtime.thread.reset(messages);
        }
      } catch (error) {
        console.error("Failed to reload conversation history:", error);
      }
    },
    [runtime]
  );

  /**
   * Polls backend status until READY, then reloads conversation history.
   * Cancels any previously active polling loop before starting.
   */
  const pollUntilReadyAndReload = useCallback(
    (cid: string) => {
      abortPollingRef.current?.();
      let aborted = false;
      abortPollingRef.current = () => {
        aborted = true;
      };

      setIsRunningRemotely(true);

      (async () => {
        while (!aborted) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          if (aborted) return;

          try {
            const resp = await api.getConversationStatus(cid);
            if (aborted) return;

            if (!isInProgress(resp.status)) {
              setIsRunningRemotely(false);
              setIsCancelling(false);
              await reloadHistory(cid);
              return;
            }
          } catch (error) {
            console.error("Failed to poll conversation status:", error);
          }
        }
      })();
    },
    [api, reloadHistory]
  );

  // On conversation change: reset state and check for in-progress runs.
  useEffect(() => {
    abortPollingRef.current?.();
    abortPollingRef.current = null;
    setIsCancelling(false);
    setIsRunningRemotely(false);

    if (!conversationId) return;

    let cancelled = false;

    (async () => {
      try {
        const resp = await api.getConversationStatus(conversationId);
        if (cancelled) return;

        if (isInProgress(resp.status)) {
          pollUntilReadyAndReload(conversationId);
        }
      } catch (error) {
        console.error("Failed to fetch conversation status:", error);
      }
    })();

    return () => {
      cancelled = true;
      abortPollingRef.current?.();
      abortPollingRef.current = null;
    };
  }, [api, conversationId, pollUntilReadyAndReload]);

  // Cancel handler
  const onCancelRun = useCallback(() => {
    if (!conversationId) return;

    // Suppress any abort-related errors from the dying SSE stream.
    clearError?.();

    // Always abort locally — no-op if no active stream.
    agent.abortRun();
    setIsCancelling(true);

    api.cancelMessage(conversationId).then(
      async () => {
        try {
          const resp = await api.getConversationStatus(conversationId);
          if (!isInProgress(resp.status)) {
            // Backend already finished — reload immediately.
            setIsCancelling(false);
            await reloadHistory(conversationId);
          } else {
            // Backend still winding down — poll until ready.
            pollUntilReadyAndReload(conversationId);
          }
        } catch {
          setIsCancelling(false);
        }
      },
      (error) => {
        console.error("Failed to cancel run:", error);
        setIsCancelling(false);
      }
    );
  }, [api, agent, conversationId, clearError, reloadHistory, pollUntilReadyAndReload]);

  return { isRunningRemotely, onCancelRun, isCancelling };
}
